import type { Location } from "../../types/experiment";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";

interface LocationEditorProps {
  label: string;
  value: Location;
  onChange: (value: Location) => void;
  className?: string;
}

type LocationKind = "iso_region" | "is_eu" | "place";

function getLocationKind(location: Location): LocationKind {
  if ("iso_region" in location) return "iso_region";
  if ("is_eu" in location) return "is_eu";
  return "place";
}

export function LocationEditor({ label, value, onChange, className }: LocationEditorProps) {
  const kind = getLocationKind(value);

  const handleKindChange = (newKind: LocationKind) => {
    switch (newKind) {
      case "iso_region":
        onChange({ iso_region: { iso_country: "", iso_subdivision: "" } });
        break;
      case "is_eu":
        onChange({ is_eu: false });
        break;
      case "place":
        onChange({ place: { city: "", subdivision: "", country: "" } });
        break;
    }
  };

  return (
    <div className={className}>
      <Label className="mb-1.5 block">{label}</Label>
      <Select value={kind} onValueChange={(v) => handleKindChange(v as LocationKind)}>
        <SelectTrigger className="mb-2">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="iso_region">ISO Region</SelectItem>
          <SelectItem value="is_eu">Is EU</SelectItem>
          <SelectItem value="place">Place</SelectItem>
        </SelectContent>
      </Select>
      {kind === "iso_region" && "iso_region" in value && (
        <div className="flex gap-2">
          <Input
            placeholder="Country (ISO)"
            value={value.iso_region.iso_country}
            onChange={(e) =>
              onChange({
                iso_region: { ...value.iso_region, iso_country: e.target.value },
              })
            }
          />
          <Input
            placeholder="Subdivision"
            value={value.iso_region.iso_subdivision}
            onChange={(e) =>
              onChange({
                iso_region: { ...value.iso_region, iso_subdivision: e.target.value },
              })
            }
          />
        </div>
      )}
      {kind === "is_eu" && "is_eu" in value && (
        <div className="flex items-center gap-2">
          <Switch
            checked={value.is_eu}
            onCheckedChange={(checked) => onChange({ is_eu: checked })}
          />
          <span className="text-sm text-overlay0">{value.is_eu ? "Yes" : "No"}</span>
        </div>
      )}
      {kind === "place" && "place" in value && (
        <div className="flex gap-2">
          <Input
            placeholder="City"
            value={value.place.city}
            onChange={(e) =>
              onChange({ place: { ...value.place, city: e.target.value } })
            }
          />
          <Input
            placeholder="Subdivision"
            value={value.place.subdivision}
            onChange={(e) =>
              onChange({ place: { ...value.place, subdivision: e.target.value } })
            }
          />
          <Input
            placeholder="Country"
            value={value.place.country}
            onChange={(e) =>
              onChange({ place: { ...value.place, country: e.target.value } })
            }
          />
        </div>
      )}
    </div>
  );
}
